<div class="flex h-screen w-screen bg-gray-100 overflow-hidden relative">

  <!-- LEFT PANEL — AI result display -->
  <div class="flex-grow flex flex-col overflow-hidden bg-white border-r border-gray-200">

    <!-- Left header -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white shrink-0">
      <button (click)="goBack()" class="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </button>
      <div class="h-4 w-px bg-gray-200"></div>
      <span class="text-sm font-medium text-gray-700">AI Query</span>
    </div>

    <!-- Result area -->
    <div class="flex-grow overflow-auto p-6">

      <!-- Empty state -->
      <div *ngIf="!activeResult" class="flex flex-col items-center justify-center h-full text-center">
        <div class="w-20 h-20 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-700 mb-2">No results yet</h3>
        <p class="text-sm text-gray-400 max-w-xs">Ask a question in the chat on the right — your results will appear here.</p>
        <button
          (click)="openExplorer()"
          class="mt-6 flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
          </svg>
          Explore your data
        </button>
      </div>

      <!-- Primary result + inline dashboard -->
      <div *ngIf="displayedResult" class="flex flex-col gap-6 pb-4">
        <!-- Banner when viewing an older result -->
        <div *ngIf="pinnedResult && pinnedResult !== activeResult"
          class="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
          <span>Viewing a previous result</span>
          <button (click)="pinnedResult = null" class="font-medium underline hover:text-amber-900">Show latest</button>
        </div>

        <!-- Primary chart/table -->
        <app-html-renderer
          [htmlContent]="displayedResult.rawHtml || ''"
          [baseStyles]="baseStyles">
        </app-html-renderer>

        <!-- Dashboard loading indicator -->
        <div *ngIf="isDashboardLoading && !dashboardHtml" class="flex items-center gap-2.5 px-1 py-2 text-xs text-gray-400">
          <svg class="animate-spin h-4 w-4 text-indigo-400 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ dashboardStep || 'Building deeper insights...' }}
        </div>

        <!-- Inline dashboard -->
        <app-html-renderer
          *ngIf="dashboardHtml"
          [htmlContent]="dashboardHtml"
          [baseStyles]="baseStyles">
        </app-html-renderer>
      </div>

    </div>

    <!-- Visualization re-query bar -->
    <div *ngIf="displayedResult" class="shrink-0 border-t border-gray-200 px-4 py-3 bg-gray-50 flex gap-2">
      <input
        type="text"
        class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
        placeholder="Change visualization — e.g. show as pie chart, group by date..."
        [(ngModel)]="visualizationQuery"
        [disabled]="isVisualizationLoading"
        (keyup.enter)="executeVisualizationQuery()">
      <button
        (click)="executeVisualizationQuery()"
        [disabled]="isVisualizationLoading || !visualizationQuery.trim()"
        class="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 shrink-0">
        <svg *ngIf="!isVisualizationLoading" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <svg *ngIf="isVisualizationLoading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Visualize
      </button>
    </div>

  </div>

  <!-- RIGHT PANEL — Chat -->
  <div class="w-[380px] shrink-0 flex flex-col bg-gray-50 overflow-hidden">

    <!-- Chat header -->
    <div class="shrink-0 px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
      <div>
        <p class="text-sm font-semibold text-gray-800">AI Assistant</p>
        <div class="flex items-center gap-1.5 mt-0.5">
          <span class="text-xs" [class.text-indigo-500]="dashboardMode === 'auto'" [class.text-gray-400]="dashboardMode === 'off'">
            {{ dashboardMode === 'auto' ? '✦ Auto insights on' : 'Auto insights off' }}
          </span>
          <button (click)="toggleDashboardMode()"
            class="relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full transition-colors duration-200"
            [class.bg-indigo-500]="dashboardMode === 'auto'"
            [class.bg-gray-300]="dashboardMode === 'off'">
            <span class="inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform duration-200 mt-0.5"
              [class.translate-x-3.5]="dashboardMode === 'auto'"
              [class.translate-x-0.5]="dashboardMode === 'off'">
            </span>
          </button>
        </div>
      </div>
      <div class="flex items-center gap-1">
        <!-- Schema explorer toggle -->
        <button
          (click)="openExplorer(); showHintsPanel = false; showFlagPanel = false"
          class="p-1.5 rounded-lg transition-colors text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
          title="Explore your data">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
          </svg>
        </button>
        <!-- Hints toggle -->
        <button
          (click)="showHintsPanel = !showHintsPanel"
          [class.text-indigo-600]="showHintsPanel || hints.length > 0"
          class="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors relative">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
          </svg>
          <span *ngIf="hints.length > 0" class="absolute -top-0.5 -right-0.5 h-3 w-3 bg-indigo-500 rounded-full text-white text-[8px] flex items-center justify-center">{{ hints.length }}</span>
        </button>
        <!-- Thumbs-up (confirm correct result) -->
        <button
          *ngIf="generatedQueries.length > 0"
          (click)="submitConfirm()"
          [disabled]="confirmed || isConfirming"
          [title]="confirmed ? 'Marked as correct — thank you!' : 'This result is correct'"
          class="p-1.5 rounded-lg transition-colors"
          [ngClass]="confirmed ? 'text-green-500 bg-green-50' : 'text-gray-400 hover:text-green-500 hover:bg-green-50'">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
          </svg>
        </button>
        <!-- Flag conversation -->
        <button
          *ngIf="chatHistory.length > 0"
          (click)="flagged ? null : (showFlagPanel = !showFlagPanel)"
          [disabled]="flagged"
          [title]="flagged ? 'Flagged' : 'Report a problem with this conversation'"
          class="p-1.5 rounded-lg transition-colors"
          [ngClass]="flagged ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 7l2.55 2.4A1 1 0 0116 11H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clip-rule="evenodd" />
          </svg>
        </button>
        <!-- New conversation -->
        <button
          *ngIf="chatHistory.length > 0"
          (click)="resetConversation()"
          class="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          title="New conversation">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
          </svg>
        </button>
        <!-- Test chart -->
        <button
          (click)="injectTestChart()"
          class="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors"
          title="Inject test chart">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Hints panel -->
    <div *ngIf="showHintsPanel" class="shrink-0 bg-indigo-50 border-b border-indigo-100 px-4 py-3">
      <p class="text-xs font-medium text-indigo-700 mb-2">Hints sent with every query</p>
      <div class="flex flex-wrap gap-1.5 mb-2">
        <span *ngFor="let hint of hints; let i = index"
          class="flex items-center gap-1 bg-white border border-indigo-200 text-indigo-700 text-xs rounded-full px-2.5 py-0.5">
          {{ hint }}
          <button (click)="removeHint(i)" class="text-indigo-300 hover:text-red-500 leading-none">&times;</button>
        </span>
        <span *ngIf="hints.length === 0" class="text-xs text-indigo-400 italic">No hints yet.</span>
      </div>
      <div class="flex gap-1.5">
        <input
          type="text"
          [(ngModel)]="newHint"
          (keyup.enter)="addHint()"
          placeholder="Add a hint..."
          class="flex-1 text-xs px-2.5 py-1 border border-indigo-200 rounded-full focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white">
        <button
          (click)="addHint()"
          [disabled]="!newHint.trim()"
          class="text-xs px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full disabled:bg-gray-300 transition-colors">
          Add
        </button>
      </div>
    </div>

    <!-- Flag panel -->
    <div *ngIf="showFlagPanel" class="shrink-0 bg-red-50 border-b border-red-100 px-4 py-3">
      <p class="text-xs font-medium text-red-700 mb-2">What went wrong?</p>
      <textarea
        [(ngModel)]="flagNote"
        placeholder="Describe the issue (optional)..."
        rows="2"
        class="w-full text-xs px-2.5 py-1.5 border border-red-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-400 bg-white resize-none mb-2"></textarea>
      <div class="flex gap-1.5 justify-end">
        <button
          (click)="showFlagPanel = false; flagNote = ''"
          class="text-xs px-3 py-1 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-full transition-colors">
          Cancel
        </button>
        <button
          (click)="submitFlag()"
          [disabled]="isFlagging"
          class="text-xs px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-full disabled:bg-gray-300 transition-colors">
          {{ isFlagging ? 'Submitting...' : 'Submit' }}
        </button>
      </div>
    </div>

    <!-- Messages -->
    <div #chatContainer class="flex-grow overflow-y-auto px-3 py-4 space-y-3">

      <!-- Welcome -->
      <div *ngIf="chatHistory.length === 0" class="flex flex-col items-center justify-center h-full text-center px-4">
        <div class="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <p class="text-sm font-semibold text-gray-700 mb-1">Ask about your data</p>
        <p class="text-xs text-gray-400 mb-4">Results will appear on the left</p>
        <div class="space-y-1.5 w-full mb-4">
          <button (click)="userQuery = 'Show me all records from the last 7 days'" class="w-full text-left text-xs bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-gray-500">
            Show all records from the last 7 days
          </button>
          <button (click)="userQuery = 'Give me a breakdown by category'" class="w-full text-left text-xs bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-gray-500">
            Breakdown by category
          </button>
          <button (click)="userQuery = 'Give me a summary of this week\'s activity'" class="w-full text-left text-xs bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-gray-500">
            Summary of this week's activity
          </button>
        </div>
        <!-- Disclaimer -->
        <div class="w-full bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-left">
          <p class="text-xs font-medium text-amber-700 mb-0.5">Heads up</p>
          <p class="text-xs text-amber-600 leading-relaxed">Results are for reference only — always verify against your source data. AI-generated queries and visualizations may not be perfectly accurate.</p>
        </div>
      </div>

      <ng-container *ngFor="let message of chatHistory">

        <!-- User -->
        <div *ngIf="message.type === 'user'" class="flex justify-end">
          <div class="bg-indigo-600 text-white rounded-2xl rounded-tr-sm py-2 px-3 text-xs max-w-[85%] shadow-sm">
            {{ message.content }}
          </div>
        </div>

        <!-- Loading -->
        <div *ngIf="message.type === 'loading'" class="flex justify-start">
          <div class="bg-white border border-gray-200 rounded-2xl rounded-tl-sm py-2.5 px-3 shadow-sm flex items-center gap-2 max-w-[85%]">
            <div class="flex space-x-1 shrink-0">
              <div class="h-1.5 w-1.5 bg-indigo-400 rounded-full animate-bounce" style="animation-delay:0ms"></div>
              <div class="h-1.5 w-1.5 bg-indigo-400 rounded-full animate-bounce" style="animation-delay:150ms"></div>
              <div class="h-1.5 w-1.5 bg-indigo-400 rounded-full animate-bounce" style="animation-delay:300ms"></div>
            </div>
            <span class="text-xs text-gray-500">{{ message.content }}</span>
            <button *ngIf="abortController" (click)="cancelExecution()"
              class="ml-1 text-xs text-red-400 hover:text-red-600 border border-red-200 rounded-full px-1.5 py-0.5 shrink-0">
              Cancel
            </button>
          </div>
        </div>

        <!-- Error -->
        <div *ngIf="message.type === 'error'" class="flex justify-start">
          <div class="bg-red-50 border border-red-200 text-red-700 rounded-2xl rounded-tl-sm py-2 px-3 text-xs max-w-[85%]">
            {{ message.content }}
          </div>
        </div>

        <!-- Clarify -->
        <div *ngIf="message.type === 'clarify'" class="flex justify-start">
          <div class="bg-amber-50 border border-amber-200 rounded-2xl rounded-tl-sm py-2 px-3 shadow-sm text-xs text-gray-700 max-w-[85%]">
            <span class="text-amber-600 font-medium block mb-0.5">Clarification needed</span>
            {{ message.content }}
          </div>
        </div>

        <!-- Plan -->
        <div *ngIf="message.type === 'plan'" class="flex justify-start">
          <div class="bg-white border border-indigo-200 rounded-2xl rounded-tl-sm py-2.5 px-3 shadow-sm text-xs text-gray-700 max-w-[85%]">
            <span class="text-indigo-600 font-medium block mb-2">Here's what I'll do</span>
            <ul class="space-y-1.5 mb-0.5">
              <li *ngFor="let line of planLines(message.content); trackBy: trackByIndex" class="flex items-start gap-2">
                <span class="mt-0.5 h-1.5 w-1.5 rounded-full bg-indigo-400 shrink-0"></span>
                <span class="leading-relaxed">{{ line }}</span>
              </li>
            </ul>
            <div *ngIf="pendingExecution" class="flex gap-1.5 mt-2.5 pt-2 border-t border-indigo-100">
              <button (click)="executePlan()"
                class="flex items-center gap-1 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-full transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
                </svg>
                Execute
              </button>
              <button (click)="cancelPlan()"
                class="px-3 py-1 text-gray-500 hover:text-gray-700 text-xs font-medium rounded-full border border-gray-200 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>

        <!-- Empty result -->
        <div *ngIf="message.type === 'empty'" class="flex justify-start">
          <div class="bg-gray-50 border border-gray-200 rounded-2xl rounded-tl-sm py-2.5 px-3 shadow-sm text-xs text-gray-600 max-w-[85%]">
            <span class="font-medium text-gray-700 block mb-1">No data found</span>
            <span>{{ message.content }}</span>
            <details *ngIf="message.sql" class="mt-2">
              <summary class="cursor-pointer text-gray-400 hover:text-gray-600 select-none">Show SQL</summary>
              <pre class="mt-1.5 p-2 bg-white border border-gray-200 rounded text-[10px] overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">{{ message.sql }}</pre>
            </details>
          </div>
        </div>

        <!-- Result (compact indicator) -->
        <div *ngIf="message.type === 'result'" class="flex flex-col gap-1.5">
          <div class="flex justify-start">
            <div class="bg-green-50 border border-green-200 rounded-2xl rounded-tl-sm py-2 px-3 shadow-sm text-xs text-gray-700 max-w-[85%] flex items-center gap-2">
              <div class="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
              <span class="text-green-700 font-medium flex-grow">Results ready</span>
              <button
                (click)="viewResult(message)"
                [class.bg-green-600]="displayedResult === message"
                [class.text-white]="displayedResult === message"
                [class.bg-white]="displayedResult !== message"
                [class.text-green-700]="displayedResult !== message"
                class="shrink-0 border border-green-300 rounded-full px-2 py-0.5 hover:bg-green-600 hover:text-white transition-colors font-medium">
                {{ displayedResult === message ? 'Viewing' : 'View' }}
              </button>
            </div>
          </div>
          <!-- Follow-up suggestions -->
          <div *ngIf="message.suggestions?.length" class="flex flex-col gap-1 pl-1">
            <span class="text-[10px] text-gray-400 uppercase tracking-wider">You could ask</span>
            <div class="flex flex-wrap gap-1.5">
              <button
                *ngFor="let q of message.suggestions"
                (click)="useSuggestion(q)"
                class="text-left text-xs px-2.5 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:border-indigo-400 transition-colors">
                {{ q }}
              </button>
            </div>
          </div>
        </div>

      </ng-container>
    </div>

    <!-- Input -->
    <div class="shrink-0 bg-white border-t border-gray-200 px-3 py-3">
      <button
        (click)="openExplorer()"
        class="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg border border-dashed border-indigo-300 text-indigo-500 hover:bg-indigo-50 hover:border-indigo-400 transition-colors text-xs font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
        </svg>
        Not sure what to ask? Explore your data →
      </button>
      <div class="flex gap-2">
        <input
          type="text"
          class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
          placeholder="Ask something..."
          [(ngModel)]="userQuery"
          [disabled]="isLoading"
          (keyup.enter)="sendMessage()">
        <button
          (click)="sendMessage()"
          [disabled]="isLoading || !userQuery.trim()"
          class="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center shrink-0">
          <svg *ngIf="!isLoading" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
          </svg>
          <svg *ngIf="isLoading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </button>
      </div>
    </div>

  </div>

</div>
